// SPDX-License-Identifier: GPL-3.0-or-later
// Callout lines and message lines of the SigmaBots table (THEME.md section 10).
//
// The rules have one output channel for text: `RulesContext.animate(target, animation)`.
// - target CALLOUT_TARGET, animation = a callout id or a message id: the client shows
//   `calloutText(animation)` as white text on black, and writes it to the ARIA live region.
// - target HUD_TARGET, animation = `timer:<s left>:<s total>` or `goal:<key>:<n>`:
//   the client shows `hudText(animation)` in the status strip.
// The engine event `ball_saved` has no rule call. The client shows CALLOUT_LINES.ball_saved.
// A callout never comments on the skill of the player. No import of three or DOM.

import { parseAnimation } from "./animation.ts";
import { modeLabel } from "./modes.ts";

/** The 20 callouts of THEME.md section 10. Each line has 6 words or fewer. */
export const CALLOUT_LINES = {
  deny_not_today: "DENY. THE BOTS HOLD.",
  accept_ask_first: "ACCEPT ALL? BOTS ASK FIRST.",
  works_on_my_machine: "BOT ORBIT. LOOP DONE.",
  lgtm: "LGTM. LARRY SIGNS OFF.",
  context_window_full: "CARL'S TANK IS FULL.",
  rate_limited: "RANDY SAYS WAIT.",
  target_missing: "THAT TARGET IS A GHOST.",
  ten_x_engineer: "TESSA SPINS. 10X BOT!",
  friday_deploy_started: "FRIDAY 16:20. BOTS DEPLOY.",
  deployed: "DEPLOYED. THE BOTS CHEER.",
  rolled_back: "ROLLED BACK. NOTHING LOST.",
  kickback_denied: "KICKBACK. DENNIS SAYS DENY.",
  ball_saved: "BALL SAVED. GUARD BOT HELD.",
  token_burn_full: "TOKEN BURN. METER FULL.",
  agent_swarm: "SIGMABOT SWARM. MULTIBALL!",
  jackpot_approved: "JACKPOT. THE BOTS APPROVE.",
  merged_no_review: "MERGE RAMPS. BOT COMBO!",
  skill_shot: "SKILL SHOT. BOTS SALUTE.",
  ask_mode: "ASK MODE. YOU PICK.",
  deleted_nothing: "THE BOTS DELETED NOTHING.",
} as const;

export type CalloutId = keyof typeof CALLOUT_LINES;

/** Fixed message lines (no argument). The text of a failed Context Window mode is exact from THEME.md. */
export const MESSAGE_LINES = {
  context_over: "CONTEXT WINDOW FULL. PLEASE START OVER.",
  mode_start_lit: "SAUCER: MODE START IS LIT",
  lock_lit: "SAUCER: LOCK IS LIT",
  super_jackpot_lit: "SAUCER: SUPER JACKPOT IS LIT",
  wizard_lit: "SAUCER: ASK MODE IS LIT",
  grand_lit: "SAUCER: GRAND APPROVAL IS LIT",
  combo: "COMBO!",
  super_skill: "SUPER SKILL SHOT!",
  tilt: "TILT. THE BALL ENDS.",
  kickback_lit: "KICKBACK IS LIT AGAIN",
} as const;

export type MessageId = keyof typeof MESSAGE_LINES;

/** Names of the four orbit and ramp shots, for jackpot and wizard lines. */
export const SHOT_NAMES = {
  rampLeft: "GHOST RAMP",
  rampRight: "MERGE RAMP",
  orbitLeft: "BOT ORBIT",
  orbitRight: "10X ORBIT",
} as const;

export type ShotId = keyof typeof SHOT_NAMES;

/** Add thousands separators to a whole number. The result does not depend on the locale. */
export function formatPoints(n: number): string {
  const sign = n < 0 ? "-" : "";
  const digits = String(Math.floor(Math.abs(n)));
  let out = "";
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += ",";
    out += digits[i];
  }
  return sign + out;
}

/** The full end-of-ball bonus line. The display shows the whole calculation. */
export function formatBonusLine(shots: number, multiplier: number): string {
  return `BONUS: ${shots} X 1,000 X ${multiplier} = ${formatPoints(shots * 1000 * multiplier)}`;
}

function isKey<T extends object>(obj: T, key: string): key is Extract<keyof T, string> {
  return Object.hasOwn(obj, key);
}

/**
 * Text for an animation string on the CALLOUT_TARGET channel.
 * Return null when the string is not a known callout or message.
 * Supported forms: a callout id, a message id, `tilt_warning:<n>`, `select:<modeId>`,
 * `lock:<n>`, `jackpot_lit:<shotId>`, `mode_done:<modeId>`, `clock:<seconds>`, `bonus:<shots>x<multiplier>`.
 */
export function calloutText(animation: string): string | null {
  const { name, arg } = parseAnimation(animation);
  if (arg === "") return plainCalloutText(name);
  switch (name) {
    case "tilt_warning":
      return `TILT WARNING ${arg} OF 2`;
    case "select":
      return `MODE: ${modeLabel(arg)}`;
    case "lock":
      return `BALL ${arg} LOCKED`;
    case "jackpot_lit":
      return isKey(SHOT_NAMES, arg) ? `JACKPOT LIT: ${SHOT_NAMES[arg]}` : null;
    case "mode_done":
      return `${modeLabel(arg)} COMPLETE`;
    case "clock":
      return clockText(arg);
    case "bonus":
      return bonusText(arg);
    default:
      return null;
  }
}

/** Text for a callout id or a message id that has no argument. */
function plainCalloutText(name: string): string | null {
  if (isKey(CALLOUT_LINES, name)) return CALLOUT_LINES[name];
  if (isKey(MESSAGE_LINES, name)) return MESSAGE_LINES[name];
  return null;
}

/** Bot Friday Deploy: the clock runs from 16:20 to 17:00 in 40 s. The arg is the seconds so far. */
function clockText(arg: string): string {
  const minutes = 20 + Math.min(40, Math.max(0, Math.floor(Number(arg) || 0)));
  const hh = 16 + Math.floor(minutes / 60);
  const mm = String(minutes % 60).padStart(2, "0");
  return `FRIDAY ${hh}:${mm}`;
}

/** The end-of-ball bonus line for `<shots>x<multiplier>`. Null when the arg has another form. */
function bonusText(arg: string): string | null {
  const m = /^(\d{1,4})x(\d)$/.exec(arg);
  return m ? formatBonusLine(Number(m[1]), Number(m[2])) : null;
}

/** Goal text templates for the status strip. `{n}` is the number in the animation string. */
export const GOAL_LINES = {
  drop_tokens: "DROP {n} MORE TOKENS",
  make_shots: "MAKE {n} MORE ORBIT OR RAMP SHOTS",
  make_orbits: "MAKE {n} MORE ORBITS",
  score_points: "SCORE {n} MORE POINTS",
  deny_shots: "DENY {n} MORE SHOTS",
  lit_jackpot: "SHOOT THE LIT JACKPOT",
  super_jackpot: "SHOOT THE SAUCER: SUPER JACKPOT",
  grand_approval: "SHOOT THE SAUCER: GRAND APPROVAL",
  none: "",
} as const;

/** One parsed update for the status strip. */
export type HudUpdate =
  | { readonly kind: "timer"; readonly secondsLeft: number; readonly secondsTotal: number }
  | { readonly kind: "goal"; readonly text: string };

/**
 * Parse an animation string on the HUD_TARGET channel.
 * Forms: `timer:<s left>:<s total>` and `goal:<key>` or `goal:<key>:<n>`.
 * Return null when the string is not valid.
 */
export function hudText(animation: string): HudUpdate | null {
  const { name, arg } = parseAnimation(animation);
  if (name === "timer") {
    const m = /^(\d{1,4}):(\d{1,4})$/.exec(arg);
    if (!m) return null;
    return { kind: "timer", secondsLeft: Number(m[1]), secondsTotal: Number(m[2]) };
  }
  if (name === "goal") {
    const sep = arg.indexOf(":");
    const key = sep < 0 ? arg : arg.slice(0, sep);
    const n = sep < 0 ? "" : arg.slice(sep + 1);
    if (!isKey(GOAL_LINES, key)) return null;
    return { kind: "goal", text: GOAL_LINES[key].replace("{n}", formatPoints(Number(n) || 0)) };
  }
  return null;
}
